import { useEffect, useRef, useState } from 'react';

import type { BillingPeriod, SubscriptionInfo } from '@hotjar/subscription-management';
import { useSubscriptionInfo } from '@hotjar/subscription-management';
import type { SiteAccount } from '@hotjar/account';
import { isBillingV3Enabled } from '@hotjar/webapp/domains/billing/features';
import { useAnnualBillingEligibility } from './useAnnualBillingEligibility';

interface UseBillingPeriodState {
  value: BillingPeriod;
  setValue: (newValue: BillingPeriod) => void;
  isLoading: boolean;
  isChanged: boolean;
}

const getValueFromBillingCycle = (
  siteAccount: Partial<SiteAccount> | undefined,
  subscription: SubscriptionInfo | undefined,
  isBillingV3: boolean,
): BillingPeriod | undefined => {
  if (isBillingV3) {
    return subscription?.customer?.subscription.billingPeriod;
  }

  const billingCycle = siteAccount?.billingCycle;
  if (billingCycle) {
    if (billingCycle === 'monthly') {
      return 'MONTHLY';
    }

    return 'ANNUALLY';
  }

  return undefined;
};

// default billing period for any account
const DEFAULT_ACCOUNT_BILLING_PERIOD = 'MONTHLY';

// default billing period used in the state value
const DEFAULT_BILLING_PERIOD = 'ANNUALLY';

export const useBillingPeriodState = (
  siteAccount: Pick<SiteAccount, 'billingCycle' | 'id' | 'features' | 'adminIds'> | undefined,
): UseBillingPeriodState => {
  const { isEligible: isAnnualEligible } = useAnnualBillingEligibility(siteAccount);
  // if annual billing is not eligible, for example because current user is a non-owner, we have to default to MONTHLY billing
  const defaultBillingPeriod = isAnnualEligible
    ? DEFAULT_BILLING_PERIOD
    : DEFAULT_ACCOUNT_BILLING_PERIOD;
  const isBillingV3 = isBillingV3Enabled(siteAccount);
  const { isLoading: isLoadingSubscription, data: subscription } = useSubscriptionInfo({
    enabled: isBillingV3,
  });

  const initialBillingPeriod = useRef<BillingPeriod | undefined>(undefined);
  const [value, setValue] = useState<BillingPeriod>(
    getValueFromBillingCycle(siteAccount, subscription, isBillingV3) || DEFAULT_BILLING_PERIOD,
  );

  useEffect(() => {
    const currentBillingPeriod = getValueFromBillingCycle(siteAccount, subscription, isBillingV3);
    setValue(currentBillingPeriod || defaultBillingPeriod);
    initialBillingPeriod.current = currentBillingPeriod || DEFAULT_ACCOUNT_BILLING_PERIOD;
  }, [isBillingV3, siteAccount, subscription, defaultBillingPeriod]);

  return {
    value,
    setValue,
    isLoading: isLoadingSubscription,
    isChanged: initialBillingPeriod.current !== undefined && initialBillingPeriod.current !== value,
  };
};
